<template>
  <div class="email-tracking-container">
    <!-- Statistics Cards - Clean Design -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      <!-- Total Sent -->
      <div class="bg-black/60 backdrop-blur-lg border border-white/20 rounded-2xl p-5 shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
        <p class="text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Total Sent</p>
        <p class="text-4xl font-bold text-white">{{ stats.sent }}</p>
      </div>
      
      <!-- Delivery Rate -->
      <div class="bg-black/60 backdrop-blur-lg border border-white/20 rounded-2xl p-5 shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
        <p class="text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Delivery Rate</p>
        <p class="text-4xl font-bold text-green-400">{{ stats.deliveryRate }}%</p>
        <p class="text-xs text-gray-400 mt-2">{{ stats.delivered }} of {{ stats.sent }} delivered</p>
      </div>
      
      <!-- Open Rate -->
      <div class="bg-black/60 backdrop-blur-lg border border-white/20 rounded-2xl p-5 shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
        <p class="text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Open Rate</p>
        <p class="text-4xl font-bold text-blue-400">{{ stats.openRate }}%</p>
        <p class="text-xs text-gray-400 mt-2">{{ stats.opened }} customers opened</p>
      </div>
      
      <!-- Click Rate -->
      <div class="bg-black/60 backdrop-blur-lg border border-white/20 rounded-2xl p-5 shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
        <p class="text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Click Rate</p>
        <p class="text-4xl font-bold text-blue-400">{{ stats.clickRate }}%</p>
        <p class="text-xs text-gray-400 mt-2">{{ stats.clicked }} customers clicked</p>
      </div>
    </div>
    
    <!-- Tracking Table - Apple Glass Style -->
    <div class="bg-black/60 backdrop-blur-lg border border-white/20 rounded-2xl overflow-hidden shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
      <!-- Table Header with Controls -->
      <div class="px-6 py-4 border-b border-white/10">
        <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h3 class="text-lg font-bold text-white">Email Tracking</h3>
            <p class="text-sm text-gray-300 mt-1">
              <span v-if="trackingData.length === 0" class="text-yellow-400">
                ⚠️ No tracking data yet - Click "Sync Data" to load from SendGrid
              </span>
              <span v-else>
                Monitor delivery and engagement for welcome emails
              </span>
            </p>
          </div>
          
          <!-- Action Buttons -->
          <div class="flex flex-wrap gap-2">
            <!-- Sync Missing Button - Only show when there are emails without tracking -->
            <button
              v-if="emailsWithoutTracking.length > 0"
              @click="syncMissingTracking"
              :disabled="isSyncingMissing || isLoading || isSyncing"
              class="inline-flex items-center px-4 py-2 bg-gradient-to-t from-orange-500 to-yellow-500 hover:from-orange-600 hover:to-yellow-600 text-white text-sm font-medium rounded-lg transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed animate-pulse ring-2 ring-orange-400 ring-offset-2 ring-offset-black/60"
              title="Get tracking data for emails with no status"
            >
              <svg
                class="w-4 h-4 mr-2"
                :class="{ 'animate-spin': isSyncingMissing }"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
              </svg>
              {{ isSyncingMissing ? 'Syncing...' : `Get Tracking (${emailsWithoutTracking.length})` }}
            </button>
            
            <!-- Sync Button (fetch from SendGrid API) -->
            <button
              @click="syncEmailTracking"
              :disabled="isSyncing || isLoading || isSyncingMissing"
              class="inline-flex items-center px-4 py-2 bg-gradient-to-t from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white text-sm font-medium rounded-lg transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              :class="{ 'animate-pulse ring-2 ring-blue-400 ring-offset-2 ring-offset-black/60': trackingData.length === 0 && !isSyncing && emailsWithoutTracking.length === 0 }"
              title="Sync all tracking data from SendGrid"
            >
              <svg
                class="w-4 h-4 mr-2"
                :class="{ 'animate-spin': isSyncing }"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
              </svg>
              {{ isSyncing ? 'Syncing...' : 'Sync All Data' }}
            </button>
            
            <!-- Refresh Button (load from cache) -->
            <button
              @click="fetchEmailTracking"
              :disabled="isLoading || isSyncing || isSyncingMissing"
              class="inline-flex items-center px-4 py-2 bg-black/40 hover:bg-black/60 border border-white/20 text-white text-sm font-medium rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              title="Refresh from cache"
            >
              <svg
                class="w-4 h-4 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
              </svg>
              Refresh
            </button>
          </div>
        </div>
      </div>
      
      <!-- Loading State -->
      <div v-if="isLoading" class="px-6 py-12 text-center">
        <svg class="w-12 h-12 mx-auto text-blue-400 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
        </svg>
        <p class="text-gray-300 mt-4">Loading email tracking data...</p>
      </div>
      
      <!-- Error State -->
      <div v-else-if="error" class="px-6 py-12 text-center">
        <div class="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-500/20 mb-4">
          <svg class="w-8 h-8 text-red-400" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
          </svg>
        </div>
        <p class="text-red-400 font-semibold text-lg mb-2">{{ error }}</p>
        <button
          @click="fetchEmailTracking"
          class="mt-4 px-4 py-2 text-blue-400 hover:text-blue-300 font-medium transition-colors"
        >
          Try Again
        </button>
      </div>
      
      <!-- Empty State -->
      <div v-else-if="enrichedSubmissions.length === 0" class="px-6 py-12 text-center">
        <div class="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-500/20 mb-4">
          <svg class="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
          </svg>
        </div>
        <p class="text-gray-300 mt-4">No email tracking data available for this period</p>
      </div>
      
      <!-- Email List - Mobile Friendly Card Layout -->
      <div v-else class="divide-y divide-white/5">
        <div
          v-for="(submission, index) in paginatedSubmissions"
          :key="index"
          class="px-4 sm:px-6 py-4 hover:bg-white/5 transition-colors"
        >
          <!-- Mobile & Desktop Layout -->
          <div class="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
            
            <!-- Status Badge - Left Side -->
            <div class="flex-shrink-0">
              <EmailStatusBadge
                v-if="submission.emailTracking"
                :tracking="submission.emailTracking"
              />
              <span v-else class="inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-500/20 text-gray-400 border border-gray-500/30 whitespace-nowrap">
                No tracking data
              </span>
            </div>
            
            <!-- Content - Right Side -->
            <div class="flex-1 min-w-0">
              <!-- Customer Name -->
              <div class="text-sm font-medium text-white truncate">
                {{ submission['1'] || 'N/A' }}
              </div>
              
              <!-- Email & Date -->
              <div class="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 mt-1">
                <div class="text-sm text-gray-300 truncate">
                  {{ submission['2'] || 'N/A' }}
                </div>
                <div class="hidden sm:block text-gray-500">•</div>
                <div class="text-xs text-gray-400 whitespace-nowrap">
                  {{ formatDate(submission.date_created) }}
                </div>
              </div>
            </div>
            
          </div>
        </div>
      </div>
      
      <!-- Pagination Controls -->
      <div v-if="enrichedSubmissions.length > entriesPerPage" class="px-6 py-4 border-t border-white/10 bg-black/20">
        <div class="flex flex-col sm:flex-row items-center justify-between gap-4">
          <!-- Page info -->
          <div class="text-sm text-gray-300">
            <div>Page {{ currentPage }} of {{ totalPages }}</div>
            <div class="text-xs text-gray-400 mt-1">
              Showing {{ (currentPage - 1) * entriesPerPage + 1 }}-{{
                Math.min(
                  currentPage * entriesPerPage,
                  enrichedSubmissions.length
                )
              }}
              of {{ enrichedSubmissions.length }}
            </div>
          </div>

          <!-- Pagination buttons -->
          <div class="flex items-center gap-2">
            <button
              @click="prevPage"
              :disabled="currentPage <= 1"
              class="px-4 py-2 rounded-lg text-sm font-medium transition-all"
              :class="
                currentPage <= 1
                  ? 'bg-gray-500/20 text-gray-500 cursor-not-allowed'
                  : 'bg-white/10 text-white hover:bg-white/20'
              "
            >
              Previous
            </button>

            <!-- Page number indicator -->
            <div class="px-4 py-2 bg-blue-500/20 text-blue-300 rounded-lg text-sm font-semibold min-w-[60px] text-center">
              {{
                currentPage
              }}
            </div>

            <button
              @click="nextPage"
              :disabled="currentPage >= totalPages"
              class="px-4 py-2 rounded-lg text-sm font-medium transition-all"
              :class="
                currentPage >= totalPages
                  ? 'bg-gray-500/20 text-gray-500 cursor-not-allowed'
                  : 'bg-white/10 text-white hover:bg-white/20'
              "
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- SMS Tracking Section -->
    <div class="bg-black/60 backdrop-blur-lg border border-white/20 rounded-2xl overflow-hidden shadow-[0_8px_32px_rgba(0,0,0,0.3)] mt-6">
      <!-- SMS Header with Controls -->
      <div class="px-6 py-4 border-b border-white/10">
        <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h3 class="text-lg font-bold text-white">SMS Tracking</h3>
            <p class="text-sm text-gray-300 mt-1">
              <span v-if="smsTrackingData.length === 0" class="text-yellow-400">
                ⚠️ No SMS tracking data yet - Click "Backfill SMS" to load from Twilio
              </span>
              <span v-else>
                Monitor delivery status for welcome SMS messages
              </span>
            </p>
          </div>
          
          <!-- SMS Action Buttons -->
          <div class="flex flex-wrap gap-2">
            <!-- Backfill SMS Button -->
            <button
              @click="backfillSMSTracking"
              :disabled="isBackfillingSMS || isLoadingSMS || isSyncingSMS"
              class="inline-flex items-center px-4 py-2 bg-gradient-to-t from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white text-sm font-medium rounded-lg transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              :class="{ 'animate-pulse ring-2 ring-green-400 ring-offset-2 ring-offset-black/60': smsTrackingData.length === 0 && !isBackfillingSMS }"
              title="Fetch SMS tracking data from Twilio (last 30 days)"
            >
              <svg
                class="w-4 h-4 mr-2"
                :class="{ 'animate-spin': isBackfillingSMS }"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"/>
              </svg>
              {{ isBackfillingSMS ? 'Backfilling...' : 'Backfill SMS (Last 30)' }}
            </button>
            
            <!-- Refresh SMS Button -->
            <button
              @click="fetchSMSTracking"
              :disabled="isLoadingSMS || isBackfillingSMS || isSyncingSMS"
              class="inline-flex items-center px-4 py-2 bg-black/40 hover:bg-black/60 border border-white/20 text-white text-sm font-medium rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              title="Refresh SMS data from cache"
            >
              <svg
                class="w-4 h-4 mr-2"
                :class="{ 'animate-spin': isLoadingSMS }"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
              </svg>
              {{ isLoadingSMS ? 'Loading...' : 'Refresh' }}
            </button>
          </div>
        </div>
      </div>

      <!-- SMS Statistics -->
      <div v-if="smsTrackingData.length > 0" class="grid grid-cols-1 sm:grid-cols-3 gap-4 px-6 py-4 bg-white/5">
        <div>
          <p class="text-xs text-gray-400 uppercase tracking-wider mb-1">Total SMS</p>
          <p class="text-2xl font-bold text-white">{{ smsStats.total }}</p>
        </div>
        <div>
          <p class="text-xs text-gray-400 uppercase tracking-wider mb-1">Delivered</p>
          <p class="text-2xl font-bold text-green-400">{{ smsStats.delivered }}</p>
        </div>
        <div>
          <p class="text-xs text-gray-400 uppercase tracking-wider mb-1">Failed</p>
          <p class="text-2xl font-bold text-red-400">{{ smsStats.failed }}</p>
        </div>
      </div>

      <!-- SMS Table -->
      <div v-if="smsTrackingData.length > 0" class="overflow-x-auto">
        <table class="w-full">
          <thead>
            <tr class="bg-white/5">
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Phone</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Status</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Sent At</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-white/5">
            <tr v-for="(sms, index) in paginatedSMSData" :key="index" class="hover:bg-white/5 transition-colors">
              <td class="px-6 py-4 text-sm text-gray-300">{{ sms.phoneNumber }}</td>
              <td class="px-6 py-4">
                <SMSStatusBadge :tracking="sms" @click="showSMSDetail(sms)" />
              </td>
              <td class="px-6 py-4 text-sm text-gray-300">{{ formatDate(sms.lastEventDate) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- SMS Empty State -->
      <div v-else class="px-6 py-12 text-center">
        <svg class="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"/>
        </svg>
        <h3 class="text-lg font-semibold text-white mb-2">No SMS Tracking Data</h3>
        <p class="text-gray-300 mb-4">Click "Backfill SMS" to fetch tracking data from Twilio</p>
      </div>

      <!-- SMS Pagination -->
      <div v-if="smsTrackingData.length > 0" class="px-6 py-4 border-t border-white/10 flex justify-between items-center">
        <div class="text-sm text-gray-300">
          Showing {{ ((currentSMSPage - 1) * smsEntriesPerPage) + 1 }} to {{ Math.min(currentSMSPage * smsEntriesPerPage, smsTrackingData.length) }} of {{ smsTrackingData.length }} SMS
        </div>
        <div class="flex items-center gap-2">
          <button
            @click="prevSMSPage"
            :disabled="currentSMSPage <= 1"
            class="px-4 py-2 rounded-lg text-sm font-medium transition-all"
            :class="currentSMSPage <= 1 ? 'bg-gray-500/20 text-gray-500 cursor-not-allowed' : 'bg-white/10 text-white hover:bg-white/20'"
          >
            Previous
          </button>
          <div class="px-4 py-2 bg-green-500/20 text-green-300 rounded-lg text-sm font-semibold min-w-[60px] text-center">
            {{ currentSMSPage }}
          </div>
          <button
            @click="nextSMSPage"
            :disabled="currentSMSPage >= totalSMSPages"
            class="px-4 py-2 rounded-lg text-sm font-medium transition-all"
            :class="currentSMSPage >= totalSMSPages ? 'bg-gray-500/20 text-gray-500 cursor-not-allowed' : 'bg-white/10 text-white hover:bg-white/20'"
          >
            Next
          </button>
        </div>
      </div>
    </div>

    <!-- SMS Detail Modal -->
    <SMSDetailModal
      :isOpen="showSMSModal"
      :sms="selectedSMS"
      @close="closeSMSModal"
    />
  </div>
</template>

<script>
import axios from 'axios';
import EmailStatusBadge from './EmailStatusBadge.vue';
import SMSStatusBadge from './SMSStatusBadge.vue';
import SMSDetailModal from './SMSDetailModal.vue';

export default {
  name: 'EmailTracking',
  components: {
    EmailStatusBadge,
    SMSStatusBadge,
    SMSDetailModal
  },
  props: {
    accountSlug: {
      type: String,
      required: true
    },
    submissions: {
      type: Array,
      default: () => []
    },
    formId: {
      type: [String, Number],
      default: null
    }
  },
  data() {
    return {
      isLoading: false,
      isSyncing: false,
      isSyncingMissing: false,
      error: null,
      trackingData: [],
      // SMS Tracking
      smsTrackingData: [],
      isLoadingSMS: false,
      isBackfillingSMS: false,
      isSyncingSMS: false,
      currentSMSPage: 1,
      smsEntriesPerPage: 10,
      // SMS Modal
      showSMSModal: false,
      selectedSMS: {},
      stats: {
        total: 0,
        sent: 0,
        delivered: 0,
        opened: 0,
        clicked: 0,
        bounced: 0,
        deliveryRate: 0,
        openRate: 0,
        clickRate: 0
      },
      currentPage: 1,
      entriesPerPage: 20
    };
  },
  computed: {
    enrichedSubmissions() {
      // Combine submission data with email tracking data
      return this.submissions.map(submission => {
        const customerEmail = submission['2'];
        
        // Defensive check: ensure trackingData is an array
        const trackingArray = Array.isArray(this.trackingData) ? this.trackingData : [];
        
        // Match on 'email' field (from sync function) - normalize both to lowercase for comparison
        const tracking = trackingArray.find(t => 
          t.email && customerEmail && 
          t.email.toLowerCase() === customerEmail.toLowerCase()
        );
        
        return {
          ...submission,
          emailTracking: tracking || null
        };
      });
    },
    
    // List of emails that don't have tracking data
    emailsWithoutTracking() {
      return this.enrichedSubmissions
        .filter(submission => !submission.emailTracking && submission['2'])
        .map(submission => submission['2'])
        .filter(email => email && email.includes('@'));
    },
    
    totalPages() {
      return Math.max(1, Math.ceil(this.enrichedSubmissions.length / this.entriesPerPage));
    },
    
    paginatedSubmissions() {
      const start = (this.currentPage - 1) * this.entriesPerPage;
      const end = start + this.entriesPerPage;
      return this.enrichedSubmissions.slice(start, end);
    },
    
    // SMS Computed Properties
    smsStats() {
      if (!this.smsTrackingData || this.smsTrackingData.length === 0) {
        return {
          total: 0,
          delivered: 0,
          failed: 0,
          totalCost: '0.00'
        };
      }
      
      const delivered = this.smsTrackingData.filter(sms => sms.delivered).length;
      const failed = this.smsTrackingData.filter(sms => sms.status === 'failed' || sms.status === 'undelivered').length;
      const totalCost = this.smsTrackingData.reduce((sum, sms) => sum + (parseFloat(sms.cost) || 0), 0);
      
      return {
        total: this.smsTrackingData.length,
        delivered,
        failed,
        totalCost: Math.abs(totalCost).toFixed(2)
      };
    },
    
    totalSMSPages() {
      return Math.max(1, Math.ceil(this.smsTrackingData.length / this.smsEntriesPerPage));
    },
    
    paginatedSMSData() {
      const start = (this.currentSMSPage - 1) * this.smsEntriesPerPage;
      const end = start + this.smsEntriesPerPage;
      return this.smsTrackingData.slice(start, end);
    }
  },
  watch: {
    submissions() {
      this.fetchEmailTracking();
    },
    // Auto-sync when emails without tracking are detected
    emailsWithoutTracking(newVal) {
      if (newVal.length > 0 && !this.isSyncing && !this.isSyncingMissing && !this.isLoading) {
        console.log(`🔄 Auto-syncing ${newVal.length} emails without tracking data...`);
        this.syncMissingTracking();
      }
    }
  },
  mounted() {
    this.fetchEmailTracking();
    this.fetchSMSTracking();
  },
  methods: {
    async fetchEmailTracking() {
      if (!this.accountSlug || this.submissions.length === 0) {
        return;
      }
      
      this.isLoading = true;
      this.error = null;
      
      try {
        // Extract customer emails from submissions - get ALL emails, not just last 20
        const allEmails = this.submissions
          .map(s => s['2'])
          .filter(email => email && email.includes('@'));
        
        if (allEmails.length === 0) {
          this.trackingData = [];
          this.stats = this.getEmptyStats();
          return;
        }
        
        console.log(`Loading tracking for all ${allEmails.length} emails`);
        
        // Try to load tracking data from cache first - load ALL emails
        const response = await axios.post('/.netlify/functions/sync-email-tracking', {
          formId: this.formId,
          emails: allEmails,  // Load all emails, not just last 20
          loadOnly: true  // Try cached data first
        });
        
        // Parse the tracking data
        this.trackingData = Array.isArray(response.data.tracking) ? response.data.tracking : [];
        this.stats = this.calculateStats(this.trackingData, allEmails.length);
        
        console.log(`Loaded tracking for ${this.trackingData.length} emails`);
        
      } catch (error) {
        console.error('Error fetching email tracking:', error);
        this.error = 'Click "Sync Data" to load tracking information';
        this.trackingData = [];
        this.stats = this.getEmptyStats();
      } finally {
        this.isLoading = false;
      }
    },
    
    getEmptyStats() {
      return {
        total: 0,
        sent: 0,
        delivered: 0,
        opened: 0,
        clicked: 0,
        bounced: 0,
        deliveryRate: 0,
        openRate: 0,
        clickRate: 0
      };
    },
    
    calculateStats(trackingData, totalEmails) {
      const sent = trackingData.length;
      const delivered = trackingData.filter(t => t.delivered).length;
      const opened = trackingData.filter(t => t.opened).length;
      const clicked = trackingData.filter(t => t.clicked).length;
      const bounced = trackingData.filter(t => t.bounced).length;
      
      return {
        total: totalEmails,
        sent: sent,
        delivered: delivered,
        opened: opened,
        clicked: clicked,
        bounced: bounced,
        deliveryRate: sent > 0 ? ((delivered / sent) * 100).toFixed(1) : 0,
        openRate: delivered > 0 ? ((opened / delivered) * 100).toFixed(1) : 0,
        clickRate: opened > 0 ? ((clicked / opened) * 100).toFixed(1) : 0
      };
    },
    
    async syncMissingTracking() {
      if (!this.accountSlug || this.emailsWithoutTracking.length === 0) {
        return;
      }
      
      this.isSyncingMissing = true;
      this.error = null;
      
      try {
        const formId = this.formId;
        
        if (!formId) {
          this.error = 'Form ID not found for this account';
          return;
        }
        
        console.log(`🔄 Syncing tracking for ${this.emailsWithoutTracking.length} emails without tracking data...`);
        
        // Call sync function with ONLY the emails that are missing tracking
        const response = await axios.post('/.netlify/functions/sync-email-tracking', {
          formId: formId,
          emails: this.emailsWithoutTracking  // Only sync missing emails
        });
        
        console.log('✅ Missing tracking sync complete:', response.data);
        console.log(`📊 Found tracking for ${response.data.trackedCount} of ${this.emailsWithoutTracking.length} emails`);
        
        if (response.data.note) {
          console.log('ℹ️', response.data.note);
        }
        
        // Show user-friendly message based on results
        if (response.data.trackedCount === 0) {
          if (response.data.note && response.data.note.includes('API key not configured')) {
            this.error = 'SendGrid API key not configured. Contact your administrator to set up tracking.';
          } else {
            this.error = 'No tracking data found for these emails. They may have been sent before tracking was enabled.';
          }
        }
        
        // After sync, refresh the data
        await this.fetchEmailTracking();
        
      } catch (error) {
        console.error('Error syncing missing tracking:', error);
        this.error = error.response?.data?.error || 'Failed to sync missing tracking data';
      } finally {
        this.isSyncingMissing = false;
      }
    },
    
    async syncEmailTracking() {
      if (!this.accountSlug || this.submissions.length === 0) {
        return;
      }
      
      this.isSyncing = true;
      this.error = null;
      
      try {
        // Use formId from props (passed from parent)
        const formId = this.formId;
        
        if (!formId) {
          this.error = 'Form ID not found for this account';
          return;
        }
        
        // Extract emails from submissions (same logic as fetchEmailTracking)
        const emails = this.submissions
          .map(s => s['2'])
          .filter(email => email && email.includes('@'));
        
        if (emails.length === 0) {
          this.error = 'No email addresses found in submissions';
          return;
        }
        
        console.log(`🔄 Syncing email tracking data for form ${formId}...`);
        
        // Call sync function with emails (no GF API call needed!)
        const response = await axios.post('/.netlify/functions/sync-email-tracking', {
          formId: formId,
          emails: emails  // Pass emails directly to avoid GF API call
        });
        
        console.log('✅ Sync complete:', response.data);
        console.log(`📊 Synced ${response.data.trackedCount} of ${response.data.emailCount} emails`);
        
        if (response.data.note) {
          console.log('ℹ️', response.data.note);
        }
        
        // Show user-friendly message based on results
        if (response.data.trackedCount === 0) {
          if (response.data.note && response.data.note.includes('API key not configured')) {
            this.error = 'SendGrid API key not configured. Contact your administrator to set up tracking.';
          } else {
            this.error = 'No tracking data available. Emails may have been sent before tracking was enabled.';
          }
        }
        
        // After sync, refresh the data
        await this.fetchEmailTracking();
        
      } catch (error) {
        console.error('Error syncing email tracking:', error);
        this.error = error.response?.data?.error || 'Failed to sync tracking data';
      } finally {
        this.isSyncing = false;
      }
    },
    
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch (error) {
        return dateString;
      }
    },
    
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    },
    
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    },
    
    // SMS Tracking Methods
    async fetchSMSTracking() {
      if (!this.formId || this.isLoadingSMS) {
        return;
      }
      
      this.isLoadingSMS = true;
      
      try {
        const response = await axios.post('/.netlify/functions/sync-sms-tracking', {
          formId: this.formId
        });
        
        if (response.data.success && response.data.data) {
          // Convert data object to array
          this.smsTrackingData = Object.values(response.data.data);
          console.log(`📱 Loaded ${this.smsTrackingData.length} SMS tracking records`);
        } else {
          this.smsTrackingData = [];
        }
      } catch (error) {
        console.error('Error fetching SMS tracking:', error);
        this.smsTrackingData = [];
      } finally {
        this.isLoadingSMS = false;
      }
    },
    
    async backfillSMSTracking() {
      if (!this.formId || this.isBackfillingSMS) {
        return;
      }
      
      this.isBackfillingSMS = true;
      
      try {
        console.log(`📱 Backfilling SMS tracking data for form ${this.formId}...`);
        
        const response = await axios.post('/.netlify/functions/backfill-twilio-tracking', {
          formId: this.formId,
          limit: 100
        });
        
        console.log('✅ SMS Backfill complete:', response.data);
        
        if (response.data.success) {
          // Check if stats exist (they won't if no phones were found)
          if (response.data.stats) {
            console.log(`📊 Processed ${response.data.stats.phonesProcessed} phones, found data for ${response.data.stats.phonesWithData}`);
          } else {
            console.log(`⚠️ ${response.data.message || 'No phone numbers found'}`);
          }
          
          // Refresh SMS tracking data
          await this.fetchSMSTracking();
        }
      } catch (error) {
        console.error('Error backfilling SMS tracking:', error);
        this.error = error.response?.data?.error || 'Failed to backfill SMS tracking';
      } finally {
        this.isBackfillingSMS = false;
      }
    },
    
    prevSMSPage() {
      if (this.currentSMSPage > 1) {
        this.currentSMSPage--;
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    },
    
    nextSMSPage() {
      if (this.currentSMSPage < this.totalSMSPages) {
        this.currentSMSPage++;
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    },
    
    showSMSDetail(sms) {
      this.selectedSMS = sms;
      this.showSMSModal = true;
    },
    
    closeSMSModal() {
      this.showSMSModal = false;
    }
  }
};
</script>

<style scoped>
.email-tracking-container {
  margin-top: 2rem;
}
</style>

